import Logger from '@core/scripts/components/logger';
import type {DyEvent} from '@dynamic-yield/scripts/components/dynamic-yield/dy-event-types';

const FLUSH_INTERVAL_MS = 250;
const READINESS_CAP_MS = 15000;

let consentGranted = false;
let queue: DyEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | undefined;
let deadline = 0;
let gaveUp = false;
let loggedMissingConsent = false;

export function initDyEventSender(hasConsent: boolean): void {
    consentGranted = hasConsent;
    gaveUp = false;
    loggedMissingConsent = false;
    discardQueue();
}

export function setDyEventConsent(hasConsent: boolean): void {
    consentGranted = hasConsent;
    discardQueue();
}

export function sendDyEvent(event: DyEvent): void {
    if (!consentGranted) {
        if (!loggedMissingConsent) {
            loggedMissingConsent = true;
            Logger.debug('dynamic yield: event dropped, no consent');
        }
        return;
    }

    if (queue.length === 0 && isDyApiReady()) {
        callDyApi(event);
        return;
    }

    if (gaveUp) {
        return;
    }

    queue.push(event);
    startFlushing();

    if (isDyApiReady()) {
        flush();
    }
}

function isDyApiReady(): boolean {
    return typeof window.DY?.API === 'function';
}

function callDyApi(event: DyEvent): void {
    window.DY?.API?.('event', event);
}

function startFlushing(): void {
    if (flushTimer !== undefined) {
        return;
    }

    deadline = Date.now() + READINESS_CAP_MS;
    flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);
}

function flush(): void {
    if (isDyApiReady()) {
        const buffered = queue;
        queue = [];
        stopFlushing();
        buffered.forEach(callDyApi);
        return;
    }

    if (Date.now() >= deadline) {
        Logger.info(`dynamic yield: DY.API unavailable, dropped ${queue.length} queued event(s)`);
        gaveUp = true;
        discardQueue();
    }
}

function discardQueue(): void {
    queue = [];
    stopFlushing();
}

function stopFlushing(): void {
    if (flushTimer === undefined) {
        return;
    }
    clearInterval(flushTimer);
    flushTimer = undefined;
}
